// HTTP-Client gegen stempel-api. Feldnamen sind bewusst snake_case -- muessen exakt zu den
// Rust-Structs passen (CheckinRequest/DeviceEnrollRequest, kein serde-Rename dort).

const API_URL = import.meta.env.VITE_STEMPEL_API_URL

async function parseErrorBody(response: Response): Promise<string> {
  try {
    return await response.text()
  } catch {
    return response.statusText
  }
}

export interface EnrollDeviceRequest {
  enrollmentCode: string
  devicePublicKeyRaw: string
  deviceLabel?: string
}

export interface EnrollDeviceResponse {
  deviceId: string
  tenantSlug: string
}

export async function enrollDevice(req: EnrollDeviceRequest): Promise<EnrollDeviceResponse> {
  const response = await fetch(`${API_URL}/api/v1/devices/enroll`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      enrollment_code: req.enrollmentCode,
      device_public_key: req.devicePublicKeyRaw,
      device_label: req.deviceLabel,
    }),
  })

  if (!response.ok) {
    throw new Error(`Enrollment fehlgeschlagen (${response.status}): ${await parseErrorBody(response)}`)
  }

  const body = (await response.json()) as { device_id: string; tenant_slug: string }
  return { deviceId: body.device_id, tenantSlug: body.tenant_slug }
}

export interface SubmitCheckinRequest {
  eventId: string
  deviceId: string
  tenantSlug: string
  employeeIdentifier: string
  logType: 'IN' | 'OUT' | 'ANFAHRT' | 'ABFAHRT'
  clientTimeIso: string
  monotonicCounter: number
  deviceSignature: string
  geoLat?: number
  geoLon?: number
  auftragId?: string
  taetigkeitGeplant?: string
  taetigkeitIst?: string
  zusatzgeraet?: string
}

export interface SubmitCheckinResponse {
  eventId: string
  status: string
  alreadyKnown: boolean
  // Nur /me/checkins (TQ #556, stempel-api #188); aeltere Server liefern sie nicht.
  konflikt?: boolean
  konfliktGrund?: string
}

/** Wirft bei 4xx/5xx -- der Aufrufer (lib/sync.ts) entscheidet anhand des Fehlers, ob und
 * wann erneut versucht wird (Netzwerkfehler/5xx = ja, 4xx = eher nicht). */
export async function submitCheckin(req: SubmitCheckinRequest): Promise<SubmitCheckinResponse> {
  const response = await fetch(`${API_URL}/api/v1/checkins`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      event_id: req.eventId,
      device_id: req.deviceId,
      tenant_slug: req.tenantSlug,
      employee_identifier: req.employeeIdentifier,
      log_type: req.logType,
      client_time: req.clientTimeIso,
      monotonic_counter: req.monotonicCounter,
      geo_lat: req.geoLat,
      geo_lon: req.geoLon,
      device_signature: req.deviceSignature,
      auftrag_id: req.auftragId,
      taetigkeit_geplant: req.taetigkeitGeplant,
      taetigkeit_ist: req.taetigkeitIst,
      zusatzgeraet: req.zusatzgeraet,
    }),
  })

  if (!response.ok) {
    const body = await parseErrorBody(response)
    const error = new Error(`Checkin fehlgeschlagen (${response.status}): ${body}`) as Error & {
      status?: number
    }
    error.status = response.status
    throw error
  }

  const body = (await response.json()) as {
    event_id: string
    status: string
    already_known: boolean
  }
  return { eventId: body.event_id, status: body.status, alreadyKnown: body.already_known }
}

// --- Rapport-Digitalisierung (TQ #18899/#18900) ---
// Diese beiden Endpunkte sind Geraete-authentifiziert per `X-Device-Id`-Header (leichtere
// Pruefung als der signierte Checkin-Pfad, siehe stempel-api auth::DeviceIdentity) --
// bewusst kein Signatur-Kontrakt wie bei canonicalMessage/submitCheckin.

export interface ApiAuftrag {
  auftragId: string
  auftragsnummer: string
  auftraggeberName: string
  auftragszeitraumVon: string | null
  auftragszeitraumBis: string | null
  auftragsort: string | null
  gewerk: 'erdbau' | 'abbruch' | 'transport' | 'sonstiges'
  status: 'offen' | 'abgeschlossen'
}

export async function listOpenAuftraege(deviceId: string): Promise<ApiAuftrag[]> {
  const response = await fetch(`${API_URL}/api/v1/auftraege`, {
    headers: { 'X-Device-Id': deviceId },
  })

  if (!response.ok) {
    throw new Error(`Auftraege laden fehlgeschlagen (${response.status}): ${await parseErrorBody(response)}`)
  }

  const body = (await response.json()) as Array<{
    auftrag_id: string
    auftragsnummer: string
    auftraggeber_name: string
    auftragszeitraum_von: string | null
    auftragszeitraum_bis: string | null
    auftragsort: string | null
    gewerk: ApiAuftrag['gewerk']
    status: ApiAuftrag['status']
  }>

  return body.map((a) => ({
    auftragId: a.auftrag_id,
    auftragsnummer: a.auftragsnummer,
    auftraggeberName: a.auftraggeber_name,
    auftragszeitraumVon: a.auftragszeitraum_von,
    auftragszeitraumBis: a.auftragszeitraum_bis,
    auftragsort: a.auftragsort,
    gewerk: a.gewerk,
    status: a.status,
  }))
}

export interface SubmitSignaturRequest {
  deviceId: string
  auftragId: string
  unterschriebenVon: 'auftraggeber' | 'auftragnehmer'
  unterzeichnerName: string
  signaturPngBase64: string
}

export interface SubmitSignaturResponse {
  auftragId: string
  unterschriebenVon: 'auftraggeber' | 'auftragnehmer'
  signedAt: string
  auftragAbgeschlossen: boolean
}

export async function submitSignatur(req: SubmitSignaturRequest): Promise<SubmitSignaturResponse> {
  const response = await fetch(`${API_URL}/api/v1/auftraege/${req.auftragId}/signaturen`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Device-Id': req.deviceId },
    body: JSON.stringify({
      unterschrieben_von: req.unterschriebenVon,
      unterzeichner_name: req.unterzeichnerName,
      signatur_png_base64: req.signaturPngBase64,
    }),
  })

  if (!response.ok) {
    throw new Error(`Signatur fehlgeschlagen (${response.status}): ${await parseErrorBody(response)}`)
  }

  const body = (await response.json()) as {
    auftrag_id: string
    unterschrieben_von: 'auftraggeber' | 'auftragnehmer'
    signed_at: string
    auftrag_abgeschlossen: boolean
  }
  return {
    auftragId: body.auftrag_id,
    unterschriebenVon: body.unterschrieben_von,
    signedAt: body.signed_at,
    auftragAbgeschlossen: body.auftrag_abgeschlossen,
  }
}

/// Schliesst einen Auftrag ab ohne Auftraggeber-Unterschrift (Patric-Entscheid: ueberspringbar,
/// siehe SPEC-Rapport-Digitalisierung-Glasbrenner.md §4.7). Geraete-authentifiziert.
export async function abschliessenAuftrag(deviceId: string, auftragId: string): Promise<void> {
  const response = await fetch(`${API_URL}/api/v1/auftraege/${auftragId}/abschliessen`, {
    method: 'POST',
    headers: { 'X-Device-Id': deviceId },
  })

  if (!response.ok) {
    throw new Error(
      `Auftrag abschliessen fehlgeschlagen (${response.status}): ${await parseErrorBody(response)}`,
    )
  }
}

export interface CheckinStatusResponse {
  employee_identifier: string
  last_log_type: string | null
  last_time: string | null
  is_checked_in: boolean
}

export async function getCheckinStatus(
  deviceId: string,
  employeeIdentifier: string,
): Promise<CheckinStatusResponse> {
  const url = `${API_URL}/api/v1/checkins/status?employee_identifier=${encodeURIComponent(employeeIdentifier)}`
  const response = await fetch(url, { headers: { 'X-Device-Id': deviceId } })
  if (!response.ok) {
    throw new Error(`Status-Abfrage fehlgeschlagen (${response.status})`)
  }
  return response.json()
}

// --- Persoenliche Checkins (JWT-Bearer, kein Geraet, /me/*) ---

export interface PersonalCheckinOptions {
  /** Idempotenz-Anker (UUID); aeltere Server ignorieren das Feld. */
  eventId?: string
  /** Zeit beim Tippen (RFC3339); aeltere Server ignorieren das Feld. */
  clientTimeIso?: string
  auftragId?: string
  taetigkeitGeplant?: string
  taetigkeitIst?: string
  zusatzgeraet?: string
}

/** Checkin fuer eingeloggte Mitarbeiter gegen /me/checkins. Der Offline-Puffer
 * (lib/personalOutbox.ts) ruft das mit eventId+clientTimeIso auf. */
export async function submitPersonalCheckin(
  bearerToken: string,
  logType: 'IN' | 'OUT' | 'ANFAHRT' | 'ABFAHRT',
  opts: PersonalCheckinOptions = {},
): Promise<SubmitCheckinResponse> {
  const response = await fetch(`${API_URL}/api/v1/me/checkins`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${bearerToken}`,
    },
    body: JSON.stringify({
      event_id: opts.eventId,
      client_time: opts.clientTimeIso,
      log_type: logType,
      auftrag_id: opts.auftragId,
      taetigkeit_geplant: opts.taetigkeitGeplant,
      taetigkeit_ist: opts.taetigkeitIst,
      zusatzgeraet: opts.zusatzgeraet,
    }),
  })
  if (!response.ok) {
    const body = await parseErrorBody(response)
    const error = new Error(`Checkin fehlgeschlagen (${response.status}): ${body}`) as Error & {
      status?: number
    }
    error.status = response.status
    throw error
  }
  // Antwort tolerant lesen: ein 2xx bedeutet "angenommen", auch wenn der Body unlesbar ist.
  const body = (await response.json().catch(() => ({}))) as {
    event_id?: string
    status?: string
    already_known?: boolean
    konflikt?: boolean
    konflikt_grund?: string
  }
  return {
    eventId: body.event_id ?? '',
    status: body.status ?? '',
    alreadyKnown: body.already_known ?? false,
    konflikt: body.konflikt ?? false,
    konfliktGrund: body.konflikt_grund ?? '',
  }
}

/** Aktueller Checkin-Status fuer den eingeloggten Mitarbeiter. */
export async function getPersonalCheckinStatus(
  bearerToken: string,
): Promise<CheckinStatusResponse> {
  const response = await fetch(`${API_URL}/api/v1/me/checkins/status`, {
    headers: { Authorization: `Bearer ${bearerToken}` },
  })
  if (!response.ok) {
    throw Object.assign(new Error(`Status-Abfrage fehlgeschlagen (${response.status})`), {
      status: response.status,
    })
  }
  return response.json()
}

/** Offene Auftraege des Tenants -- JWT-Bearer-Auth, kein Geraet noetig. */
export async function listPersonalAuftraege(bearerToken: string): Promise<ApiAuftrag[]> {
  const response = await fetch(`${API_URL}/api/v1/me/auftraege`, {
    headers: { Authorization: `Bearer ${bearerToken}` },
  })
  if (!response.ok) {
    throw Object.assign(
      new Error(
        `Auftraege laden fehlgeschlagen (${response.status}): ${await parseErrorBody(response)}`,
      ),
      { status: response.status },
    )
  }
  const body = (await response.json()) as Array<{
    auftrag_id: string
    auftragsnummer: string
    auftraggeber_name: string
    auftragszeitraum_von: string | null
    auftragszeitraum_bis: string | null
    auftragsort: string | null
    gewerk: ApiAuftrag['gewerk']
    status: ApiAuftrag['status']
  }>
  return body.map((a) => ({
    auftragId: a.auftrag_id,
    auftragsnummer: a.auftragsnummer,
    auftraggeberName: a.auftraggeber_name,
    auftragszeitraumVon: a.auftragszeitraum_von,
    auftragszeitraumBis: a.auftragszeitraum_bis,
    auftragsort: a.auftragsort,
    gewerk: a.gewerk,
    status: a.status,
  }))
}

export interface TodayCheckin {
  event_id: string
  employee_identifier: string
  log_type: string
  client_time: string
  taetigkeit_geplant: string | null
  taetigkeit_ist: string | null
  zusatzgeraet: string | null
}

export async function getTodayCheckins(
  deviceId: string,
  auftragId: string,
): Promise<TodayCheckin[]> {
  const url = `${API_URL}/api/v1/auftraege/${auftragId}/checkins/today`
  const response = await fetch(url, { headers: { 'X-Device-Id': deviceId } })
  if (!response.ok) {
    throw new Error(`Tages-Eintraege fehlgeschlagen (${response.status})`)
  }
  return response.json()
}

// --- Tagesrapport auf Klick (TQ #1151, E-520/E-521): POST /me/rapporte, JWT-Bearer ---

export interface RapportSignatur {
  unterzeichnerName: string
  signaturPngBase64: string
}

export interface CreatePersonalRapportRequest {
  auftragId: string
  signaturen?: { auftraggeber?: RapportSignatur; auftragnehmer?: RapportSignatur }
}

export interface PersonalRapport {
  rapportId: string
  dateiname: string
  pfad: string
  /** Interner Nextcloud-Link; null, wenn nur der Ablage-Pfad bekannt ist. */
  nextcloudLink: string | null
  sha256: string
  bytes: number
}

/** Erzeugt den Tagesrapport des Auftrags (alle Eintraege des Tages bis zum Ausstempeln).
 * Wirft bei Fehlern ein Error mit `status` (kein Status = Netzwerkfehler). */
export async function createPersonalRapport(
  bearerToken: string,
  req: CreatePersonalRapportRequest,
): Promise<PersonalRapport> {
  const sig = (s?: RapportSignatur) =>
    s ? { unterzeichner_name: s.unterzeichnerName, signatur_png_base64: s.signaturPngBase64 } : undefined
  const signaturen = req.signaturen
    ? { auftraggeber: sig(req.signaturen.auftraggeber), auftragnehmer: sig(req.signaturen.auftragnehmer) }
    : undefined
  const response = await fetch(`${API_URL}/api/v1/me/rapporte`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${bearerToken}` },
    body: JSON.stringify({ auftrag_id: req.auftragId, signaturen }),
  })
  if (!response.ok) {
    throw Object.assign(new Error(`Rapport fehlgeschlagen (${response.status})`), {
      status: response.status,
    })
  }
  const body = (await response.json()) as {
    rapport_id: string
    dateiname: string
    pfad: string
    nextcloud_link: string | null
    sha256: string
    bytes: number
  }
  return {
    rapportId: body.rapport_id,
    dateiname: body.dateiname,
    pfad: body.pfad,
    nextcloudLink: body.nextcloud_link ?? null,
    sha256: body.sha256,
    bytes: body.bytes,
  }
}
